import React from 'react';
import styled from 'styled-components';
import { ArrowRight, Check, Mail, Building } from 'lucide-react';

interface RegistrationCompleteProps {
  teacherData: {
    firstName: string;
    lastName: string;
    email: string;
    schoolName: string;
    schoolId: string;
    teacherId: string;
  };
  onContinue: () => void;
}

// Styled Components
const Container = styled.div`
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #e6f8ef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
`;

const Title = styled.h1`
  color: #6D28D9;
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #4B5563;
  font-size: 1rem;
  margin-bottom: 2rem;
`;

const TeacherCard = styled.div`
  background-color: #FAF5FF;
  border: 1px solid #E9D5FF;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  text-align: left;
`;

const TeacherHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const InitialCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #E9D5FF;
  color: #7E22CE;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 600;
  margin-right: 0.75rem;
`;

const TeacherInfo = styled.div``;

const TeacherName = styled.p`
  color: #111827;
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
`;

const TeacherRole = styled.p`
  color: #6B7280;
  font-size: 0.75rem;
  margin: 0;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
`;

const InfoText = styled.p`
  color: #6B7280;
  font-size: 0.875rem;
  margin: 0;
`;

const MonoText = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
`;

const Button = styled.button`
  background-color: #7E22CE;
  color: white;
  font-weight: 500;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #6B21A8;
  }
`;

const StyledArrowIcon = styled(ArrowRight)`
  margin-left: 0.5rem;
`;

const RegistrationComplete: React.FC<RegistrationCompleteProps> = ({ teacherData, onContinue }) => {
  // Get the first initial for the circle display
  const getInitial = (firstName: string) => {
    return firstName.charAt(0).toUpperCase();
  };

  // Format name as "Title LastName" as shown in the screenshot
  const formatName = () => {
    if (teacherData.firstName.toLowerCase() === 'madam' || 
        teacherData.firstName.toLowerCase() === 'mr' || 
        teacherData.firstName.toLowerCase() === 'mrs' || 
        teacherData.firstName.toLowerCase() === 'miss' || 
        teacherData.firstName.toLowerCase() === 'dr') {
      return `${teacherData.firstName} ${teacherData.lastName}`;
    }
    return `${teacherData.firstName} ${teacherData.lastName}`;
  };

  const handleContinue = () => {
    // Call the onContinue prop which will trigger the dashboard redirection
    onContinue();
  };

  return (
    <Container>
      <SuccessIcon>
        <Check size={32} stroke="#10B981" strokeWidth={2} />
      </SuccessIcon>
      
      <Title>Registration Complete!</Title>
      <Subtitle>Your teacher account has been created successfully</Subtitle>
      
      <TeacherCard>
        <TeacherHeader>
          <InitialCircle>
            {getInitial(teacherData.firstName)}
          </InitialCircle>
          
          <TeacherInfo>
            <TeacherName>{formatName()}</TeacherName>
            <TeacherRole>Teacher - {teacherData.schoolName}</TeacherRole>
          </TeacherInfo>
        </TeacherHeader>
          
        <InfoContainer>
          <InfoRow>
            <Mail size={14} color="#6B7280" style={{ marginRight: '0.5rem' }} />
            <InfoText>{teacherData.email}</InfoText>
          </InfoRow>
          
          <InfoRow>
            <Building size={14} color="#6B7280" style={{ marginRight: '0.5rem' }} />
            <InfoText>Teacher ID: <MonoText>{teacherData.teacherId}</MonoText></InfoText>
          </InfoRow>
        </InfoContainer>
      </TeacherCard>

      <Button onClick={handleContinue}>
         Login to access your Teacher Dashboard
        <StyledArrowIcon size={18} />
      </Button>
    </Container>
  );
};

export default RegistrationComplete;